import React, { useEffect } from 'react';
// import { useRouter } from 'next/router';
import { useClientType } from '../hooks/useClientType';
import { useTypedSelector } from '../../store/store';

declare global {
  interface Window {
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
    dataLayer?: IArguments[];
  }
}

const SV_CRISP_WEBSITE_ID = '2611e74f-8b94-4159-957f-0f407aa2e3a4';
// const TRUBLU_CRISP_WEBSITE_ID = '2611e74f-8b94-4159-957f-0f407aa2e3a4';
//dev tb b4f2d8e6-43d2-4c04-8758-a5714b6a301b
const CrispChat: React.FC = () => {
  const { isTrubluClient } = useClientType();
  // const router = useRouter();
  const { name: userName, email } = useTypedSelector((state) => state.me);
  const { name: venueName } = useTypedSelector((state) => state.account.account);
  const isLoggedIn = useTypedSelector((state) => !!state.me.id);

  const initCrispChat = () => {
    if (window.$crisp) return;

    window.$crisp = [];
    window.CRISP_WEBSITE_ID = SV_CRISP_WEBSITE_ID;
    const injectScriptToHead = () => {
      const d = document;
      const script = d.createElement('script');
      script.src = 'https://client.crisp.chat/l.js';
      script.async = true;
      d.getElementsByTagName('head')[0].appendChild(script);
    };

    injectScriptToHead();
  };

  const updateCrispId = (newCrispId: string) => {
    if (!window.$crisp || window.CRISP_WEBSITE_ID === newCrispId) return;

    window.CRISP_WEBSITE_ID = newCrispId;
    window.$crisp.push(['do', 'session:reset']);
  };

  useEffect(() => {
    initCrispChat();
    // Set URL/session metadata immediately (does not depend on Redux data)
    if (typeof window !== 'undefined') {
      window.$crisp = window.$crisp || [];
      if (window.location) {
        window.$crisp!.push([
          'set',
          'session:data',
          [[['full_url', window.location.href]]],
        ]);
        window.$crisp!.push([
          'set',
          'session:segments',
          [[`path:${window.location.pathname}`]],
        ]);
      }
    }
  }, []);

  useEffect(() => {
    const newCrispId =  SV_CRISP_WEBSITE_ID;

    updateCrispId(newCrispId);
  }, [isTrubluClient]);

  useEffect(() => {
    
    // Always allow queuing, even if the script hasn't loaded yet
    window.$crisp = window.$crisp || [];

    const name = userName ? userName : 'Anonymous User';
    const venue = venueName ? venueName : 'Unknown';
    const pathSuffix =
      typeof window !== 'undefined' && window.location?.href ? ` on ${window.location.href}` : '';
    const desiredNickname = `(WEB-APP) [${venue}] ${name}${pathSuffix}`;

    const setUserProps = () => {
      window.$crisp!.push(['set', 'user:nickname', [desiredNickname]]);
      if (isLoggedIn && email) window.$crisp!.push(['set', 'user:email', [email]]);
      // Attach full URL and a simple path segment for filtering/automation
      if (typeof window !== 'undefined' && window.location) {
        window.$crisp!.push([
          'set',
          'session:data',
          [[['full_url', window.location.href]]],
        ]);
        window.$crisp!.push([
          'set',
          'session:segments',
          [[`path:${window.location.pathname}`]],
        ]);
      }
    };

    // Set immediately (queued and will apply when SDK is ready)
    setUserProps();

    // Delayed re-apply to catch cases where SDK loads after initial queue
    const delayedApply = setTimeout(() => {
      setUserProps();
    }, 2000);

    // Apply again after a longer delay to ensure it's set even if session loads late
    const lateApply = setTimeout(() => {
      setUserProps();
    }, 5000);

    return () => {
      clearTimeout(delayedApply);
      clearTimeout(lateApply);
    };
  }, [isLoggedIn, userName, venueName, email]);

  // Register event handlers once on mount to avoid accumulation
  useEffect(() => {
    window.$crisp = window.$crisp || [];

    const onSessionLoaded = () => {
      // Re-apply current user props when session loads
      const name = userName ? userName : 'Anonymous User';
      const venue = venueName ? venueName : 'Unknown';
      const pathSuffix =
        typeof window !== 'undefined' && window.location?.href ? ` on ${window.location.href}` : '';
      const desiredNickname = `(WEB-APP) [${venue}] ${name}${pathSuffix}`;

      window.$crisp!.push(['set', 'user:nickname', [desiredNickname]]);
      if (isLoggedIn && email) window.$crisp!.push(['set', 'user:email', [email]]);
      if (typeof window !== 'undefined' && window.location) {
        window.$crisp!.push([
          'set',
          'session:data',
          [[['full_url', window.location.href]]],
        ]);
        window.$crisp!.push([
          'set',
          'session:segments',
          [[`path:${window.location.pathname}`]],
        ]);
      }
    };

    const onChatOpened = () => {
      // Re-apply when chat opens
      const name = userName ? userName : 'Anonymous User';
      const venue = venueName ? venueName : 'Unknown';
      const pathSuffix =
        typeof window !== 'undefined' && window.location?.href ? ` on ${window.location.href}` : '';
      const desiredNickname = `(WEB-APP) [${venue}] ${name}${pathSuffix}`;

      window.$crisp!.push(['set', 'user:nickname', [desiredNickname]]);
      if (isLoggedIn && email) window.$crisp!.push(['set', 'user:email', [email]]);
    };

    window.$crisp!.push(['on', 'session:loaded', onSessionLoaded]);
    window.$crisp!.push(['on', 'chat:opened', onChatOpened]);

    return () => {
      window.$crisp!.push(['off', 'session:loaded', onSessionLoaded]);
      window.$crisp!.push(['off', 'chat:opened', onChatOpened]);
    };
  }, [isLoggedIn, userName, venueName, email]);

  return null;
};

export default CrispChat;
